// admin/src/pages/UserList.tsx
import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Typography, Box, Button, TablePagination, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Stack, Avatar, Chip, Divider
} from '@mui/material';
import { Edit, Delete, PeopleAltRounded, ForumRounded, ArticleRounded, VisibilityRounded, SearchRounded, SettingsRounded } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { fetchUsers, deleteUser, updateUser, adminListUserChatThreads, adminGetChatMessages, fetchNotes, getNoteById } from '../services/api';

type SlimUser = { _id: string; fullName?: string; email?: string; username?: string; avatar?: string };
type AdminChatThread = {
  _id: string;
  participants: SlimUser[];
  requestedBy?: SlimUser;
  requestedTo?: SlimUser;
  status: 'pending' | 'active' | 'declined';
  lastMessageAt?: string;
  lastMessageSnippet?: string;
};
type AdminChatMessage = { _id: string; sender: SlimUser; receiver: SlimUser; text: string; createdAt: string };
type NoteItem = { _id: string; slug?: string; title?: string; entryDate?: string; createdAt?: string; category?: string | null; project?: any };

const UserList: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [friendsOpen, setFriendsOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [threads, setThreads] = useState<AdminChatThread[]>([]);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [selectedThread, setSelectedThread] = useState<AdminChatThread | null>(null);
  const [messages, setMessages] = useState<AdminChatMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notes, setNotes] = useState<NoteItem[]>([]);
  const [selectedNote, setSelectedNote] = useState<NoteItem | null>(null);
  const [notesSearch, setNotesSearch] = useState('');
  const [noteDetail, setNoteDetail] = useState<any | null>(null);
  const [noteDetailLoading, setNoteDetailLoading] = useState(false);
  const [noteDetailOpen, setNoteDetailOpen] = useState(false);
  const [limitsOpen, setLimitsOpen] = useState(false);
  const [limitsUser, setLimitsUser] = useState<any | null>(null);
  const [limitsForm, setLimitsForm] = useState({ tuneUploadLimit: '', videoUploadLimit: '', tuneFileSizeLimit: '', videoFileSizeLimit: '', chatFileSizeLimit: '' });
  const [limitsSaving, setLimitsSaving] = useState(false);
  const navigate = useNavigate();

  const displayName = (u?: SlimUser | null) => u?.fullName || u?.username || u?.email || 'Unknown user';
  const formatTime = (val?: string) => {
    if (!val) return '';
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
  };
  const formatDate = (val?: string) => {
    if (!val) return '';
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
  };
  const userLabel = (u: any): string => {
    if (!u) return '—';
    if (typeof u === 'string') return u;
    return (
      u.username ||
      u.fullName ||
      [u.firstName, u.lastName].filter(Boolean).join(' ') ||
      u.email ||
      u._id ||
      '—'
    );
  };
  const nameOf = (v: any) => (typeof v === 'object' ? (v?.name || v?.title || v?.label) : v) || '—';
  const toFileUrl = (u?: string) => {
    if (!u) return '';
    const base = (process.env.REACT_APP_BASE_URL || 'http://localhost:4000/api').replace(/\/api\/?$/, '');
    return /^https?:\/\//i.test(u) ? u : `${base}${u}`;
  };

  const otherForThread = (thread: AdminChatThread, userId: string | undefined) => {
    const uid = userId || '';
    return (thread.participants || []).find((p) => p._id !== uid) || thread.requestedBy || thread.requestedTo || null;
  };

  const loadUsers = async () => {
    try {
      const res = await fetchUsers();
      let data: any[] = [];
      if (Array.isArray(res)) data = res;
      else if (res && Array.isArray(res.data)) data = res.data;
      else if (res && Array.isArray(res.items)) data = res.items;
      else if (res && Array.isArray(res.users)) data = res.users;

      setUsers(data);
      setFilteredUsers(data);
    } catch (err) {
      console.error('Failed to load users', err);
      setUsers([]);
      setFilteredUsers([]);
    }
  };

  const openFriends = async (user: any) => {
    const uid = user?._id || user?.id;
    if (!uid) return;
    setSelectedUser(user);
    setFriendsOpen(true);
    setChatOpen(false);
    setSelectedThread(null);
    setMessages([]);
    setThreads([]);
    setLoadingThreads(true);
    try {
      const data = await adminListUserChatThreads(uid);
      setThreads(data?.threads || []);
    } catch (err: any) {
      console.error('Failed to load friends', err);
      Swal.fire('Error', err?.response?.data?.message || 'Unable to load friends', 'error');
    } finally {
      setLoadingThreads(false);
    }
  };

  const openChat = async (thread: AdminChatThread) => {
    setSelectedThread(thread);
    setChatOpen(true);
    setMessages([]);
    setLoadingMessages(true);
    try {
      const data = await adminGetChatMessages(thread._id);
      setMessages(data?.messages || []);
    } catch (err: any) {
      console.error('Failed to load chat messages', err);
      Swal.fire('Error', err?.response?.data?.message || 'Unable to load messages', 'error');
    } finally {
      setLoadingMessages(false);
    }
  };

  const unwrap = (res: any): any[] => {
    if (!res) return [];
    const payload = Array.isArray(res) ? res : res.data ?? res;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.results)) return payload.results;
    return [];
  };

  const openNotes = async (user: any) => {
    const uid = user?._id || user?.id;
    if (!uid) return;
    setSelectedUser(user);
    setNotesOpen(true);
    setNotes([]);
    setNotesSearch('');
    setSelectedNote(null);
    setNoteDetail(null);
    setNoteDetailOpen(false);
    setNotesLoading(true);
    try {
      const res = await fetchNotes({ userId: uid, adminAll: true, includeUser: true });
      const list = unwrap(res) as NoteItem[];
      setNotes(list);
    } catch (err: any) {
      console.error('Failed to load notes', err);
      Swal.fire('Error', err?.response?.data?.message || 'Unable to load notes', 'error');
    } finally {
      setNotesLoading(false);
    }
  };

  const openNoteDetail = async (note: NoteItem) => {
    if (!note?._id) return;
    setSelectedNote(note);
    setNoteDetail(null);
    setNoteDetailLoading(true);
    setNoteDetailOpen(true);
    try {
      const data = await getNoteById(note.slug || note._id);
      setNoteDetail(data);
    } catch (err: any) {
      console.error('Failed to load note detail', err);
      Swal.fire('Error', err?.response?.data?.message || 'Unable to load note', 'error');
    } finally {
      setNoteDetailLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'User will be permanently deleted!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (!result.isConfirmed) return;

    try {
      await deleteUser(id);
      await loadUsers();
      Swal.fire('Deleted!', 'User has been deleted.', 'success');
    } catch (err) {
      console.error('Delete user failed', err);
      Swal.fire('Error', 'Unable to delete user.', 'error');
    }
  };

  const openLimits = (user: any) => {
    setLimitsUser(user);
    setLimitsForm({
      tuneUploadLimit: user.tuneUploadLimit != null ? String(user.tuneUploadLimit) : '',
      videoUploadLimit: user.videoUploadLimit != null ? String(user.videoUploadLimit) : '',
      tuneFileSizeLimit: user.tuneFileSizeLimit != null ? String(user.tuneFileSizeLimit) : '',
      videoFileSizeLimit: user.videoFileSizeLimit != null ? String(user.videoFileSizeLimit) : '',
      chatFileSizeLimit: user.chatFileSizeLimit != null ? String(user.chatFileSizeLimit) : '',
    });
    setLimitsOpen(true);
  };

  const saveLimits = async () => {
    if (!limitsUser) return;
    const tuneVal = limitsForm.tuneUploadLimit.trim();
    const videoVal = limitsForm.videoUploadLimit.trim();
    const tuneSizeVal = limitsForm.tuneFileSizeLimit.trim();
    const videoSizeVal = limitsForm.videoFileSizeLimit.trim();
    const chatSizeVal = limitsForm.chatFileSizeLimit.trim();
    const tuneNum = tuneVal === '' ? null : Number(tuneVal);
    const videoNum = videoVal === '' ? null : Number(videoVal);
    const tuneSizeNum = tuneSizeVal === '' ? null : Number(tuneSizeVal);
    const videoSizeNum = videoSizeVal === '' ? null : Number(videoSizeVal);
    const chatSizeNum = chatSizeVal === '' ? null : Number(chatSizeVal);

    if (tuneVal !== '' && (Number.isNaN(tuneNum) || (tuneNum as number) < 0)) {
      Swal.fire('Invalid', 'Tune upload limit must be a non-negative number or empty for unlimited.', 'warning');
      return;
    }
    if (videoVal !== '' && (Number.isNaN(videoNum) || (videoNum as number) < 0)) {
      Swal.fire('Invalid', 'Video upload limit must be a non-negative number or empty for unlimited.', 'warning');
      return;
    }
    if (tuneSizeVal !== '' && (Number.isNaN(tuneSizeNum) || (tuneSizeNum as number) <= 0)) {
      Swal.fire('Invalid', 'Tune file size limit must be a positive number (MB) or empty for unlimited.', 'warning');
      return;
    }
    if (videoSizeVal !== '' && (Number.isNaN(videoSizeNum) || (videoSizeNum as number) <= 0)) {
      Swal.fire('Invalid', 'Video file size limit must be a positive number (MB) or empty for unlimited.', 'warning');
      return;
    }
    if (chatSizeVal !== '' && (Number.isNaN(chatSizeNum) || (chatSizeNum as number) <= 0)) {
      Swal.fire('Invalid', 'Chat file size limit must be a positive number (MB) or empty for unlimited.', 'warning');
      return;
    }

    setLimitsSaving(true);
    try {
      await updateUser(limitsUser._id || limitsUser.id, {
        tuneUploadLimit: tuneNum,
        videoUploadLimit: videoNum,
        tuneFileSizeLimit: tuneSizeNum,
        videoFileSizeLimit: videoSizeNum,
        chatFileSizeLimit: chatSizeNum,
      });
      setLimitsOpen(false);
      await loadUsers();
      Swal.fire('Saved', 'Upload limits updated successfully.', 'success');
    } catch (err: any) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to update limits.', 'error');
    } finally {
      setLimitsSaving(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    const lower = search.toLowerCase();
    const filtered = users.filter((user) =>
      String(user.fullName || '').toLowerCase().includes(lower) ||
      String(user.email || '').toLowerCase().includes(lower) ||
      String(user.username || '').toLowerCase().includes(lower)
    );
    setFilteredUsers(filtered);
    setPage(0);
  }, [search, users]);

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <Box mb={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">All Users</Typography>
          <Button
            variant="contained"
            sx={{
              background: 'linear-gradient(to right, #6226A7, #F6A938)',
              '&:hover': { background: 'linear-gradient(to right, #4e1f84, #db8b21)' },
            }}
            onClick={() => navigate('/users/create')}
          >
            Add User
          </Button>
        </Box>
        <Box mt={2} display="flex" justifyContent="flex-start">
          <TextField
            label="Search users"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 300 }}
          />
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: '#F6A938' }}>
            <TableRow>
              <TableCell><strong>Full Name</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Username</strong></TableCell>
              <TableCell><strong>Mobile</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.length > 0 ? (
              paginatedUsers.map((user) => (
                <TableRow key={user._id || user.id}>
                  <TableCell>{user.fullName || '—'}</TableCell>
                  <TableCell>{user.email || '—'}</TableCell>
                  <TableCell>{user.username || '—'}</TableCell>
                  <TableCell>{user.mobile || '—'}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => openNotes(user)} title="Notes">
                      <ArticleRounded />
                    </IconButton>
                    <IconButton onClick={() => openFriends(user)} title="Friends">
                      <PeopleAltRounded />
                    </IconButton>
                    <IconButton onClick={() => openLimits(user)} title="Upload Limits">
                      <SettingsRounded />
                    </IconButton>
                    <IconButton onClick={() => navigate(`/users/edit/${user._id || user.id}`)}>
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(user._id || user.id)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">No users found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {filteredUsers.length > 0 && (
          <TablePagination
            component="div"
            count={filteredUsers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </TableContainer>

      {/* Notes dialog */}
      <Dialog
        open={notesOpen}
        onClose={() => { setNotesOpen(false); setSelectedNote(null); setNoteDetailOpen(false); }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Notes for {displayName(selectedUser)}</DialogTitle>
        <DialogContent dividers>
          {notesLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : notes.length === 0 ? (
            <Typography>No notes found for this user.</Typography>
          ) : (
            <Box>
              <Box mb={1.5} display="flex" alignItems="center" gap={1}>
                <TextField
                  size="small"
                  placeholder="Search notes"
                  value={notesSearch}
                  onChange={(e) => setNotesSearch(e.target.value)}
                  InputProps={{ startAdornment: <SearchRounded fontSize="small" /> }}
                  sx={{ maxWidth: 260 }}
                />
              </Box>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 440 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {notes
                      .filter((n) => (n.title || '').toLowerCase().includes(notesSearch.toLowerCase()))
                      .map((n) => (
                        <TableRow
                          key={n._id}
                          hover
                          selected={selectedNote?._id === n._id}
                          onClick={() => openNoteDetail(n)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{n.title || 'Untitled'}</TableCell>
                          <TableCell>{formatDate(n.entryDate || n.createdAt)}</TableCell>
                          <TableCell>{n.category || '—'}</TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); openNoteDetail(n); }} title="View note">
                              <VisibilityRounded fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setNotesOpen(false); setSelectedNote(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Note detail dialog */}
      <Dialog open={noteDetailOpen} onClose={() => setNoteDetailOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{noteDetail?.title || selectedNote?.title || 'Note Details'}</DialogTitle>
        <DialogContent dividers>
          {noteDetailLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : noteDetail ? (
            <Stack spacing={1.2}>
              <Typography variant="body2" color="text.secondary">
                Reminder: {formatDate(noteDetail.entryDate) || '—'} • Created: {formatDate(noteDetail.createdAt)}
              </Typography>
              <Typography variant="body2">User: {userLabel(noteDetail.user)}</Typography>
              <Typography variant="body2">Project: {nameOf(noteDetail.project)}</Typography>
              <Typography variant="body2">Category: {noteDetail.category || '—'}</Typography>
              <Typography variant="body2">Tags: {(noteDetail.tags || []).join(', ') || '—'}</Typography>
              <Typography variant="subtitle2" mt={1}>Content</Typography>
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 1,
                  backgroundColor: '#fff',
                  maxHeight: 320,
                  overflow: 'auto',
                }}
                dangerouslySetInnerHTML={{ __html: noteDetail.note || noteDetail.content || '' }}
              />
              {Array.isArray(noteDetail.images) && noteDetail.images.length > 0 && (
                <Stack spacing={0.5} mt={1}>
                  <Typography variant="subtitle2">Attachments</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {noteDetail.images.map((f: any) => (
                      <Chip
                        key={f._id || f.url}
                        label={f.filename || f.originalname || 'file'}
                        component="a"
                        href={toFileUrl(f.url)}
                        target="_blank"
                        clickable
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Stack>
                </Stack>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">No details to show.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Friends list dialog */}
      <Dialog
        open={friendsOpen}
        onClose={() => { setFriendsOpen(false); setSelectedThread(null); setChatOpen(false); }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Friends of {displayName(selectedUser)}
        </DialogTitle>
        <DialogContent dividers>
          {loadingThreads ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : threads.length === 0 ? (
            <Typography variant="body1">No friends/chats found for this user.</Typography>
          ) : (
            <Stack divider={<Divider flexItem />} spacing={1}>
              {threads.map((t) => {
                const other = otherForThread(t, selectedUser?._id || selectedUser?.id);
                return (
                  <Box key={t._id} display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar>{(displayName(other).charAt(0) || '?').toUpperCase()}</Avatar>
                      <Box>
                        <Typography fontWeight={600}>{displayName(other)}</Typography>
                        <Typography variant="body2" color="text.secondary">{other?.email || other?.username || '—'}</Typography>
                        {t.lastMessageSnippet && (
                          <Typography variant="body2" color="text.secondary">
                            {t.lastMessageSnippet} {t.lastMessageAt ? `• ${formatTime(t.lastMessageAt)}` : ''}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Chip
                        label={t.status === 'active' ? 'Active' : t.status === 'pending' ? 'Pending' : 'Declined'}
                        color={t.status === 'active' ? 'success' : t.status === 'pending' ? 'warning' : 'default'}
                        size="small"
                      />
                      <IconButton color="primary" onClick={() => openChat(t)} title="Show chats">
                        <ForumRounded />
                      </IconButton>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setFriendsOpen(false); setChatOpen(false); }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Upload limits dialog */}
      <Dialog open={limitsOpen} onClose={() => setLimitsOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Upload Limits — {displayName(limitsUser)}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} mt={0.5}>
            <TextField
              label="Tune Upload Limit (count)"
              type="number"
              value={limitsForm.tuneUploadLimit}
              onChange={(e) => setLimitsForm((prev) => ({ ...prev, tuneUploadLimit: e.target.value }))}
              placeholder="Leave empty for unlimited"
              helperText={limitsForm.tuneUploadLimit === '' ? 'Currently: Unlimited' : `Limit: ${limitsForm.tuneUploadLimit} files`}
              inputProps={{ min: 0 }}
              fullWidth
            />
            <TextField
              label="Tune File Size Limit (MB)"
              type="number"
              value={limitsForm.tuneFileSizeLimit}
              onChange={(e) => setLimitsForm((prev) => ({ ...prev, tuneFileSizeLimit: e.target.value }))}
              placeholder="Leave empty for unlimited"
              helperText={limitsForm.tuneFileSizeLimit === '' ? 'Currently: Unlimited' : `Limit: ${limitsForm.tuneFileSizeLimit} MB per file`}
              inputProps={{ min: 0, step: 0.1 }}
              fullWidth
            />
            <TextField
              label="Video Upload Limit (count)"
              type="number"
              value={limitsForm.videoUploadLimit}
              onChange={(e) => setLimitsForm((prev) => ({ ...prev, videoUploadLimit: e.target.value }))}
              placeholder="Leave empty for unlimited"
              helperText={limitsForm.videoUploadLimit === '' ? 'Currently: Unlimited' : `Limit: ${limitsForm.videoUploadLimit} files`}
              inputProps={{ min: 0 }}
              fullWidth
            />
            <TextField
              label="Video File Size Limit (MB)"
              type="number"
              value={limitsForm.videoFileSizeLimit}
              onChange={(e) => setLimitsForm((prev) => ({ ...prev, videoFileSizeLimit: e.target.value }))}
              placeholder="Leave empty for unlimited"
              helperText={limitsForm.videoFileSizeLimit === '' ? 'Currently: Unlimited' : `Limit: ${limitsForm.videoFileSizeLimit} MB per file`}
              inputProps={{ min: 0, step: 0.1 }}
              fullWidth
            />
            <TextField
              label="Chat Attachment Size Limit (MB)"
              type="number"
              value={limitsForm.chatFileSizeLimit}
              onChange={(e) => setLimitsForm((prev) => ({ ...prev, chatFileSizeLimit: e.target.value }))}
              placeholder="Leave empty for unlimited (default 50 MB)"
              helperText={limitsForm.chatFileSizeLimit === '' ? 'Currently: Unlimited (max 50 MB per file)' : `Limit: ${limitsForm.chatFileSizeLimit} MB per chat attachment`}
              inputProps={{ min: 0, step: 0.1 }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLimitsOpen(false)} disabled={limitsSaving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveLimits}
            disabled={limitsSaving}
            sx={{ background: 'linear-gradient(to right, #6226A7, #F6A938)', '&:hover': { background: 'linear-gradient(to right, #4e1f84, #db8b21)' } }}
          >
            {limitsSaving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Chat messages dialog */}
      <Dialog open={chatOpen} onClose={() => setChatOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          Chats with {displayName(selectedThread ? otherForThread(selectedThread, selectedUser?._id || selectedUser?.id) : null)}
        </DialogTitle>
        <DialogContent dividers>
          {loadingMessages ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : messages.length === 0 ? (
            <Typography>No messages found for this conversation.</Typography>
          ) : (
            <Stack spacing={1.2}>
              {messages.map((m) => {
                const viewerUserId = selectedUser?._id || selectedUser?.id || '';
                const isFromUser = String(m.sender?._id || '') === String(viewerUserId);
                const align = isFromUser ? 'flex-end' : 'flex-start'; // user's own messages on the right
                const bg = isFromUser ? 'rgba(98, 38, 167, 0.14)' : 'rgba(0,0,0,0.04)';
                const border = isFromUser ? '1px solid rgba(98, 38, 167, 0.28)' : '1px solid #e0e0e0';
                return (
                  <Box key={m._id} display="flex" justifyContent={align}>
                    <Box
                      sx={{
                        maxWidth: '75%',
                        p: 1.25,
                        borderRadius: 1.5,
                        backgroundColor: bg,
                        border,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>
                          {(displayName(m.sender).charAt(0) || '?').toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography fontWeight={600} lineHeight={1.2}>{displayName(m.sender)}</Typography>
                          <Typography variant="caption" color="text.secondary">{formatTime(m.createdAt)}</Typography>
                        </Box>
                      </Stack>
                      <Typography whiteSpace="pre-wrap">{m.text}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChatOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserList;
